const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// Pure helpers behind the Command Center content moderation page.

// What happens to a post moderation flags, by organization_ai_configs.
// human_review_required. Both values keep the moderation queue in charge.
export const FLAGGED_CONTENT_OPTIONS = [
  {
    value: true,
    label: 'Human review',
    description: 'A flagged post waits for a brand admin to approve or reject it in the moderation queue.',
  },
  {
    value: false,
    label: 'Auto-reject',
    description: 'A flagged post is rejected automatically. A brand admin can overturn the rejection in the moderation queue.',
  },
]

export function flaggedContentLabel(humanReviewRequired) {
  return humanReviewRequired === false ? 'Auto-reject' : 'Human review'
}

function flaggedContentImpact(humanReviewRequired) {
  return humanReviewRequired === false
    ? 'Flagged posts are rejected automatically; a brand admin can overturn a rejection in the moderation queue.'
    : 'Flagged posts wait for a brand admin to approve or reject them in the moderation queue.'
}

// switchChange lists what the Enable or Disable dialog changes. Disabling
// lists only the switch: it leaves what happens to flagged content alone.
// Enabling lists the switch, the approval, the posts already live that were
// never checked (when there are any; options.recheckLive is the dialog's
// checkbox), and the dialog's "Flagged content" choice
// (humanReviewRequired), which is always sent with it. The approval row
// stays incomplete, never shown as a valid proposal, until both its date and
// reference are present and the date is not in the future (options.today,
// the operator's local date, defaults to now).
export function switchChange(organization, enabled, approval = {}, humanReviewRequired, options = {}) {
  const current = organization?.enabled ? 'On' : 'Off'
  const proposed = enabled ? 'On' : 'Off'
  const rows = [{
    key: 'switch',
    label: 'Content moderation',
    current,
    proposed,
    impact: enabled
      ? 'New and edited posts from this organization are checked.'
      : 'New checks stop for this organization. Posts already held stay held: disabling releases nothing.',
  }]
  if (!enabled) return rows
  rows.push({
    key: 'approval',
    label: 'Data-processing approval',
    current: organization?.approval_date
      ? `${organization.approval_date} · ${organization.approval_reference || 'no reference'}`
      : 'Not recorded',
    proposed: approval.date || approval.reference
      ? `${approval.date || 'date missing'} · ${(approval.reference || '').trim() || 'reference missing'}`
      : 'Required',
    incomplete: !approvalValid(approval, options.today),
    impact: 'Content from this organization reaches the moderation provider only after this approval is recorded.',
  })
  const livePosts = livePostsChange(organization, options.recheckLive, humanReviewRequired)
  if (livePosts) rows.push(livePosts)
  const currentHumanReview = organization?.human_review_required !== false
  const proposedHumanReview = typeof humanReviewRequired === 'boolean' ? humanReviewRequired : currentHumanReview
  rows.push({
    key: 'flagged_content',
    label: 'Flagged content',
    current: flaggedContentLabel(currentHumanReview),
    proposed: flaggedContentLabel(proposedHumanReview),
    impact: flaggedContentImpact(proposedHumanReview),
  })
  return rows
}

export function approvalComplete(approval = {}) {
  return ISO_DATE_PATTERN.test(approval.date || '') && Boolean((approval.reference || '').trim())
}

// localToday is today's date in the operator's time zone as YYYY-MM-DD.
export function localToday(now = new Date()) {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

// approvalDateError is the field error for a typed approval date, or ''.
// Zero-padded ISO dates compare correctly as strings.
export function approvalDateError(date, today = localToday()) {
  return ISO_DATE_PATTERN.test(date || '') && date > today ? 'The approval date cannot be in the future.' : ''
}

// approvalDateIsOld reports an approval dated more than a year before today.
export function approvalDateIsOld(date, today = localToday()) {
  if (!ISO_DATE_PATTERN.test(date || '')) return false
  const yearAgo = `${Number(today.slice(0, 4)) - 1}${today.slice(4)}`
  return date < yearAgo
}

// approvalValid is what enabling needs: a complete approval whose date is
// not in the future.
export function approvalValid(approval = {}, today = localToday()) {
  return approvalComplete(approval) && !approvalDateError(approval.date, today)
}

export const OLD_APPROVAL_HINT = 'This approval is more than a year old. Check it still covers data processing before you enable moderation.'

// switchPayload is the PATCH body of the Enable and Disable dialogs.
// humanReview is { value, current }: the Enable dialog's "Flagged content"
// choice and the organization's current value. Enabling sends the choice,
// and recheck_live_posts when options.recheckLivePosts is true; disabling
// sends the switch alone (the API refuses a choice with it).
export function switchPayload(enabled, approval = {}, humanReview = {}, options = {}) {
  if (!enabled) return { enabled: false }
  const payload = { enabled: true, approval_date: approval.date, approval_reference: (approval.reference || '').trim() }
  const { value } = humanReview || {}
  if (typeof value === 'boolean') payload.human_review_required = value
  if (options.recheckLivePosts === true) payload.recheck_live_posts = true
  return payload
}

// recheckLiveLabel is the Enable dialog's checkbox label.
export function recheckLiveLabel(count) {
  return `Also check the ${count} ${plural(count, 'post', 'posts')} already live`
}

export const RECHECK_LIVE_DESCRIPTION = 'They stay live while they are checked; a post moderation flags leaves the feed. Unticked, they stay live and are marked as not checked.'

// livePostsChange is the Change row for posts published while moderation
// was off and never checked, or null when there are none.
export function livePostsChange(organization, recheck, humanReviewRequired) {
  const count = organization?.live_unchecked_posts || 0
  if (count <= 0) return null
  let impact = 'They stay live and are marked "Published before moderation, not checked".'
  if (recheck) {
    impact = humanReviewRequired === false
      ? 'They stay live while they are checked. Any that moderation flags leave the feed and are rejected automatically; a brand admin can overturn each one.'
      : 'They stay live while they are checked. Any that moderation flags leave the feed and wait for a brand admin in the moderation queue.'
  }
  return {
    key: 'live_posts',
    label: 'Posts already live',
    current: `${count} not checked`,
    proposed: recheck ? 'Checked now' : 'Left unchecked',
    impact,
  }
}

// enableSuccessMessage is the toast after enabling; response is the PATCH
// answer, whose live_posts_queued wins over the dialog's count.
export function enableSuccessMessage(organization, recheckLive, response) {
  const name = organization?.name || 'the organization'
  const queued = response?.live_posts_queued ?? organization?.live_unchecked_posts ?? 0
  if (!recheckLive || queued <= 0) return `Content moderation enabled for ${name}`
  return `Content moderation enabled for ${name}. Checking ${queued} live ${plural(queued, 'post', 'posts')}; they stay live until checked.`
}

const LABEL_KINDS = {
  tag_label: 'Tag',
  creator_name: 'Creator name',
  cta_target: 'Call-to-action link',
  caption_label: 'Caption label',
}

// labelKindWords names the kind of public label a scan match is.
export function labelKindWords(kind) {
  return LABEL_KINDS[kind] || 'Label'
}

// labelRuleWords says which rule a label matches, e.g.
// "“guaranteed winner” · brand rule · Block".
export function labelRuleWords(rule) {
  if (!rule) return ''
  const source = rule.platform === true ? 'platform rule' : 'brand rule'
  const action = rule.action === 'block' ? 'Block' : 'Flag'
  return `“${rule.phrase}” · ${source} · ${action}`
}

export const FLAGGED_CONTENT_DESCRIPTION = 'What happens to a post moderation flags for this organization. The change applies at once, also to posts already waiting, and brand admins get one notice about it.'

function plural(count, singular, pluralForm) {
  return count === 1 ? singular : pluralForm
}

// flaggedContentFlipImpact says what switching to proposed does to the
// organization's open reviews, from the counts the organizations list
// carries (open_reviews is pending only).
export function flaggedContentFlipImpact(organization, proposedHumanReview) {
  if (proposedHumanReview === false) {
    const pending = organization?.open_reviews || 0
    return pending > 0
      ? `The ${pending} ${plural(pending, 'post', 'posts')} waiting for review ${plural(pending, 'is', 'are')} rejected automatically now. A brand admin can overturn each one in the moderation queue.`
      : 'No post is waiting for review. Posts moderation flags from now on are rejected automatically.'
  }
  const autoRejected = organization?.auto_rejected_reviews || 0
  return autoRejected > 0
    ? `The ${autoRejected} auto-rejected ${plural(autoRejected, 'post goes', 'posts go')} back to the moderation queue to wait for a brand admin.`
    : 'No post was rejected automatically. Posts moderation flags from now on wait for a brand admin.'
}

// flaggedContentChange is the ChangeSet of the "Change flagged content"
// dialog: empty while the choice equals the stored one.
export function flaggedContentChange(organization, humanReviewRequired) {
  const current = organization?.human_review_required !== false
  if (typeof humanReviewRequired !== 'boolean' || humanReviewRequired === current) return []
  return [{
    key: 'flagged_content',
    label: 'Flagged content',
    current: flaggedContentLabel(current),
    proposed: flaggedContentLabel(humanReviewRequired),
    impact: flaggedContentFlipImpact(organization, humanReviewRequired),
  }]
}

// flaggedContentSuccessMessage is the toast after a flagged-content change;
// reviewsFlipped is the count the API made under the policy lock.
export function flaggedContentSuccessMessage(organization, humanReviewRequired, reviewsFlipped = 0) {
  const name = organization?.name || 'The organization'
  const flipped = reviewsFlipped || 0
  if (humanReviewRequired === false) {
    return `${name} now rejects flagged posts automatically.${flipped > 0 ? ` ${flipped} ${plural(flipped, 'post', 'posts')} waiting for review ${plural(flipped, 'was', 'were')} rejected.` : ''}`
  }
  return `${name} now sends flagged posts to brand review.${flipped > 0 ? ` ${flipped} auto-rejected ${plural(flipped, 'post is', 'posts are')} back in the queue.` : ''}`
}

// reviewQueueSummary says, in one line, what an organization's queue holds:
// pending reviews with the oldest one's age, then auto-rejections. '' when
// both are zero. formatAgeFn is formatAge from moderationReviewModel.js,
// passed in so this model keeps no import of the review model.
export function reviewQueueSummary(organization, formatAgeFn) {
  const pending = organization?.open_reviews || 0
  const autoRejected = organization?.auto_rejected_reviews || 0
  const parts = []
  if (pending > 0) {
    parts.push(`${pending} open ${plural(pending, 'review', 'reviews')} · oldest ${formatAgeFn(organization.oldest_open_review_age_seconds)}`)
  }
  if (autoRejected > 0) parts.push(`${autoRejected} auto-rejected`)
  return parts.join(' · ')
}

export function platformSummary(platform) {
  if (!platform) return ''
  if (!platform.enabled) return 'Content moderation is off for the whole platform. Organization switches take effect once it is turned on.'
  const provider = platform.provider_name || platform.provider
  return platform.mode === 'enforce'
    ? `The platform enforces moderation with ${provider}.`
    : `The platform runs moderation in shadow mode with ${provider}: results are recorded, nothing is held.`
}
